import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Barcode from "react-barcode";
import { BodyCopy } from "../components/BodyCopy";
import { Button, CircularProgress, GradientText } from "lola-framework-ui-test";
import { useStore } from "../store/globalState";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { formatHeroAmount } from "../utils";
import vanillaIcon from "../assets/vanillaIcon.png";

/** Shared strip height (px). Matches `vanillaIcon.png` intrinsic height (79). */
const BARCODE_STRIP_HEIGHT = 79;

/** POS scanners need high-contrast bars; never bind this to the partner theme. */
const BARCODE_LINE_COLOR = "#000000";

export interface BarcodeDrawerSlotProps {
  amount: number;
  currency: string;
  /**
   * The InComm Vanilla Direct (VRS) barcode value to render. This must be the
   * raw `vrsBarCode` returned by the funding endpoint — it is encoded as a real
   * scannable CODE128 symbol so the cashier's POS can read it (required for
   * InComm certification).
   */
  barcodeValue: string;
  /** Barcode TTL in minutes (drives the countdown when `expiresAt` is absent). */
  expMinutes: number;
  /** Absolute expiry timestamp (ISO 8601); preferred source for the countdown. */
  expiresAt?: string;
  /** A replacement barcode is being minted: dim the strip and show a spinner. */
  refreshing?: boolean;
  onClose: () => void;
}

/** Formats a remaining-seconds value as mm:ss. */
const formatCountdown = (totalSeconds: number): string => {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Bottom-drawer content for the cash-deposit barcode step. Shown over
 * CashStoresPage via Popup. Renders the amount to tender at the former
 * countdown scale and a live mm:ss in the expiry hint. Funding confirmation
 * is automatic (app-level SSE). While the barcode is valid, "I already paid"
 * closes this drawer the same way as the close icon. Once it expires, a
 * regenerate CTA returns to the stores screen so a new barcode can be minted.
 */
export const BarcodeDrawerSlot = memo(
  ({
    amount,
    currency,
    barcodeValue,
    expMinutes,
    expiresAt,
    refreshing = false,
    onClose,
  }: BarcodeDrawerSlotProps) => {
    const { t } = useTranslation();
    const { theme } = useStore();
    const colorConfig = theme.whiteView;
    const {
      backgroundBtn,
      textColorBtn,
      bodyCopy,
      footerColor,
      title,
      dropzoneColors,
    } = colorConfig;
    const { redirectTo } = useRouteWithToken();

    const payAmount = formatHeroAmount(amount, currency);

    // Absolute deadline for the countdown: prefer the server `expiresAt`, fall
    // back to now + expMinutes. Computed once per barcode instrument.
    const deadline = useMemo(() => {
      if (expiresAt) {
        const parsed = new Date(expiresAt).getTime();
        if (Number.isFinite(parsed)) return parsed;
      }
      return Date.now() + expMinutes * 60_000;
    }, [expiresAt, expMinutes]);

    const [remaining, setRemaining] = useState(() =>
      Math.round((deadline - Date.now()) / 1000)
    );

    useEffect(() => {
      setRemaining(Math.round((deadline - Date.now()) / 1000));
      const id = setInterval(() => {
        const next = Math.round((deadline - Date.now()) / 1000);
        setRemaining(next);
        if (next <= 0) clearInterval(id);
      }, 1000);
      return () => clearInterval(id);
    }, [deadline]);

    const expired = remaining <= 0;

    const themeVars = useMemo(
      () =>
        ({
          "--barcode-accent": backgroundBtn,
          "--barcode-card-border": bodyCopy,
          "--barcode-card-surface": colorConfig.background,
          "--barcode-strip-height": `${BARCODE_STRIP_HEIGHT}px`,
        }) as React.CSSProperties,
      [backgroundBtn, bodyCopy, colorConfig.background]
    );

    return (
      <section className="barcode-drawer-slot" style={themeVars}>
        <GradientText
          as="h2"
          textColor={bodyCopy}
          className="barcode-drawer-title"
        >
          {t("barcode.title")}
        </GradientText>

        <GradientText
          as="p"
          textColor={bodyCopy}
          className="barcode-pay-amount"
          aria-label={`${t("barcode.totalPayment")} ${payAmount.prefix}${payAmount.intPart}.${payAmount.decPart}${payAmount.suffix}`}
        >
          <span className="barcode-pay-amount__value">
            {payAmount.prefix}
            {payAmount.intPart}.{payAmount.decPart}
          </span>
          <span className="barcode-pay-amount__currency">
            {payAmount.suffix}
          </span>
        </GradientText>

        <div className="barcode-strip-row">
          <div className="barcode-strip-group">
            <img
              src={vanillaIcon}
              className="barcode-vanilla-icon"
              alt="Vanilla Direct"
            />
            <div
              className={`barcode-strip-slot${expired ? " is-expired" : ""}${
                refreshing ? " is-refreshing" : ""
              }`}
              aria-busy={refreshing}
            >
              <Barcode
                value={barcodeValue}
                format="CODE128"
                displayValue={false}
                renderer="svg"
                background="transparent"
                lineColor={BARCODE_LINE_COLOR}
                width={2}
                height={BARCODE_STRIP_HEIGHT}
                margin={0}
                className="barcode-strip"
              />
              {refreshing && (
                <div
                  className="barcode-strip-refreshing"
                  role="status"
                  aria-label={t("barcode.refreshing")}
                >
                  <CircularProgress
                    variant="loading"
                    size={40}
                    strokeWidth={4}
                    colors={dropzoneColors}
                  />
                </div>
              )}
              {expired && !refreshing && (
                <div className="barcode-strip-placeholder" role="status">
                  <BodyCopy
                    textColor={TITLE_COLOR}
                    className="barcode-strip-placeholder__label"
                  >
                    {t("barcode.expiredStripLabel")}
                  </BodyCopy>
                </div>
              )}
            </div>
          </div>
        </div>

        <GradientText
          as="p"
          textColor={bodyCopy}
          className="barcode-expiry-hint"
        >
          {refreshing
            ? t("barcode.refreshing")
            : expired
              ? t("barcode.expired")
              : t("barcode.expiryHint", { time: formatCountdown(remaining) })}
        </GradientText>

        <section className="barcode-drawer-footer">
          <BodyCopy textColor={footerColor} className="barcode-disclaimer">
            {t("barcode.disclaimerLine1")} {t("barcode.disclaimerLine2")}{" "}
            <GradientText
              as="span"
              className="barcode-terms-link"
              textColor={title}
              onClick={() => redirectTo(APP_ROUTES.LEGAL_PAGE)}
            >
              {t("barcode.termsLink")}
            </GradientText>
            .
          </BodyCopy>

          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={onClose}
          >
            {expired ? t("barcode.regenerate") : t("barcode.alreadyPaid")}
          </Button>
        </section>
      </section>
    );
  }
);
